import React, { useState } from 'react'
import { Form, Formik } from 'formik'
import * as Yup from 'yup'
import { FieldError, FieldSet, Input, Label, PasswordInput } from 'ui/acro'
import { Button, SubscriptionDisclaimer } from 'ui/bend'
import axios from 'axios'
import AccountLocationError from './common/AccountLocationError'
import useSurveyDataCookie from 'helpers/getSurveyUserData'
import {
  HAS_ALO_AUTH_TOKEN_STATE_COOKIE,
  redirect_to,
  setCookie,
  redirect_to_with_cookie_creation,
  CALLBACK_URL,
  getPath
} from 'components_v2/common'
import { sign_in_event, verify_account_event } from 'gtm/pushEvents'
import {
  change_braze_user,
  verify_account_braze_event
} from 'braze/brazeEvents'

const SignInForm = ({
  hideSignUp = false,
  hideTerms = true,
  isSignupScreen = false
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [logInFailedError, setLogInFailedError] = useState(false)
  const { FetchSurveyUserData } = useSurveyDataCookie()

  const schema = Yup.object().shape({
    email: Yup.string()
      .required('Email is required')
      .email('Invalid email format'),
    password: Yup.string()
      .required('Password is required')
      .min(6, 'Password must be at least 6 characters') // keeping the sign in form at 6 ch with no other validations so existing users passwords won't stop working
  })

  const authenticateSignIn = (data) => {
    setIsSubmitting(true)
    setLogInFailedError(false)

    axios
      .post('/api/v2/sessions', {
        ...data,
        ...(CALLBACK_URL && { redirect_to: CALLBACK_URL })
      })
      .then(function (res) {
        const accountJustLinked = Boolean(res?.data?.accountJustLinked)
        if (accountJustLinked) {
          const { existingUser, tier, user } = res?.data
          verify_account_event(existingUser, tier, user)
          verify_account_braze_event(existingUser, tier, user)
          sign_in_event({
            aa_linked_user: res?.data?.linkedUser,
            user: res?.data?.user
          })
          change_braze_user(res?.data?.user)
        } else {
          sign_in_event({
            aa_linked_user: res?.data?.linkedUser,
            user: res?.data?.user
          })
          change_braze_user(res?.data?.user)
        }
        // First link: land on home so welcome popup shows before survey
        FetchSurveyUserData('user_starts_survey')
        const redirect = accountJustLinked
          ? '/'
          : getPath(
              CALLBACK_URL ||
                res?.data?.redirect_to ||
                res?.data?.redirectTo ||
                '/'
            )

        if (res.data.alo_migrated && !HAS_ALO_AUTH_TOKEN_STATE_COOKIE) {
          fetch(`/api/v2/sso/generate_multipass_token?redirect_to=${redirect}`)
            .then((res) => res.json())
            .then((data) => {
              if (!data.retry_token && data.isAloUser && data.alo_token) {
                setCookie('alo_access_session_established', 'true', 1 / 24)
                redirect_to_with_cookie_creation(data.alo_token)
              } else {
                setCookie('alo_access_session_established', 'false', 1 / 24)
                redirect_to_with_cookie_creation(redirect)
              }
            })
        } else {
          setCookie('alo_access_session_established', 'false', 1 / 24)
          // localStorage.setItem('signupSuccess', 'true')
          if (redirect) {
            redirect_to(redirect)
          } else {
            window.location.reload(true)
          }
        }
      })
      .catch(function (res) {
        setIsSubmitting(false)
        setLogInFailedError(
          res?.response?.data?.locked
            ? res?.response?.data?.errors?.email
            : 'The email and password you entered are either incorrect or not in our records.'
        )
      })
  }
  return (
    <Formik
      validationSchema={schema}
      initialValues={{ email: '', password: '' }}
      onSubmit={authenticateSignIn}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit
      }) => (
        <Form onSubmit={handleSubmit}>
          <FieldSet>
            {logInFailedError && (
              <AccountLocationError>{logInFailedError}</AccountLocationError>
            )}
            <Label>
              <Input
                type='email'
                name='email'
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.email}
                errors={errors}
                touched={touched}
                className={touched.email && errors.email && 'error'}
                placeholder='Email Address'
              />
              {errors.email && touched.email && (
                <FieldError>{errors.email}</FieldError>
              )}
            </Label>
          </FieldSet>
          <PasswordInput
            name='password'
            showValidations={false}
            values={values}
            errors={errors}
            touched={touched}
            handleBlur={handleBlur}
            placeHolder='Password'
          />

          <SubscriptionDisclaimer
            cta={'Sign In'}
            hideTerms={hideTerms}
            mt={'8px'}
            isSignupScreen={isSignupScreen}
          />
          <Button
            type='submit'
            width='100%'
            mt='32px'
            disabled={
              (errors.email && true) ||
              (errors.password && true) ||
              !values ||
              values.length <= 0
            }
            processing={isSubmitting}
            id='sign_in_button'
          >
            Sign In
          </Button>
        </Form>
      )}
    </Formik>
  )
}

export default SignInForm
